import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { AlertTriangle, Clock, Play, Square, RefreshCw } from 'lucide-react';
import { colors, fontFamily } from '../../theme';

const card = { background: colors.cardBg, boxShadow: colors.shadowCard, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.5rem', marginBottom: '1.25rem' };
const inputStyle = { padding: '0.6rem 0.75rem', background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, fontFamily, fontSize: '0.85rem' };
const btnPrimary = { padding: '0.5rem 1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' };
const btnCancel = { padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.1)', border: 'none', color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };
const btnDanger = { padding: '0.5rem 1rem', background: 'rgba(220,53,69,0.2)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontFamily, display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' };
const btnGreen = { padding: '0.5rem 1rem', background: `${colors.odds}33`, border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 4, cursor: 'pointer', fontFamily, display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' };

export default function MaintenanceManagement() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showStartForm, setShowStartForm] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('System is under maintenance. Please check back later.');
  const [scheduleType, setScheduleType] = useState('immediate');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');

  // Site Warning state
  const [warningData, setWarningData] = useState(null);
  const [warningLoading, setWarningLoading] = useState(true);
  const [warningActionLoading, setWarningActionLoading] = useState(false);
  const [showWarningForm, setShowWarningForm] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  useEffect(() => { loadStatus(); loadWarning(); }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getMaintenanceStatusAdmin();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load maintenance status:', err);
    } finally {
      setLoading(false);
    }
  };

  const startMaintenance = async () => {
    try {
      setActionLoading(true);
      const data = { message: maintenanceMessage };
      if (scheduleType === 'scheduled' && scheduledStart) data.scheduled_start = new Date(scheduledStart).toISOString();
      if (scheduledEnd) data.scheduled_end = new Date(scheduledEnd).toISOString();
      await api.admin.startMaintenance(data);
      setShowStartForm(false);
      loadStatus();
      alert(scheduleType === 'immediate' ? 'Maintenance mode started' : 'Maintenance scheduled');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start maintenance');
    } finally {
      setActionLoading(false);
    }
  };

  const stopMaintenance = async () => {
    if (!window.confirm('Are you sure you want to end maintenance mode?')) return;
    try {
      setActionLoading(true);
      await api.admin.stopMaintenance();
      loadStatus();
      alert('Maintenance mode ended');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to stop maintenance');
    } finally {
      setActionLoading(false);
    }
  };

  const cancelScheduled = async (maintenanceId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled maintenance?')) return;
    try {
      setActionLoading(true);
      await api.admin.cancelScheduledMaintenance(maintenanceId);
      loadStatus();
      alert('Scheduled maintenance cancelled');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel maintenance');
    } finally {
      setActionLoading(false);
    }
  };

  const loadWarning = async () => {
    try {
      setWarningLoading(true);
      const data = await api.admin.getWarning();
      setWarningData(data);
      if (data.warning?.message) setWarningMessage(data.warning.message);
    } catch (err) {
      console.error('Failed to load warning:', err);
    } finally {
      setWarningLoading(false);
    }
  };

  const setWarning = async () => {
    if (!warningMessage.trim()) { alert('Please enter a warning message'); return; }
    try {
      setWarningActionLoading(true);
      await api.admin.setWarning(warningMessage.trim());
      setShowWarningForm(false);
      loadWarning();
      alert('Warning set — it will now appear on the login page');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to set warning');
    } finally {
      setWarningActionLoading(false);
    }
  };

  const clearWarning = async () => {
    if (!window.confirm('Remove the current warning from the login page?')) return;
    try {
      setWarningActionLoading(true);
      await api.admin.clearWarning();
      setWarningMessage('');
      loadWarning();
      alert('Warning cleared');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clear warning');
    } finally {
      setWarningActionLoading(false);
    }
  };

  const formatDate = (s) => s ? new Date(s).toLocaleString() : '—';

  if (loading) {
    return <div style={{ padding: '3rem', textAlign: 'center', color: colors.textMuted, fontFamily }}>Loading...</div>;
  }

  return (
    <div style={{ fontFamily, color: colors.textPrimary }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ color: colors.textPrimary, margin: 0, fontSize: '1.3rem' }}>Maintenance Mode</h2>
        <button onClick={loadStatus} style={btnPrimary}><RefreshCw size={14} /> Refresh</button>
      </div>

      {/* Current Status */}
      <div style={card}>
        <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '0.95rem' }}>Current Status</h3>

        {status?.is_active ? (
          <div style={{ background: 'rgba(220,53,69,0.08)', border: '1px solid rgba(220,53,69,0.4)', borderLeft: `4px solid ${colors.negative}`, borderRadius: 6, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <AlertTriangle size={20} style={{ color: colors.negative, flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.negative, fontWeight: 700, fontSize: '0.95rem', marginBottom: '0.35rem' }}>Maintenance Mode Active</div>
                <p style={{ color: colors.textMuted, margin: '0 0 0.5rem 0', fontSize: '0.85rem' }}>{status.current?.message}</p>
                {status.current?.scheduled_end && (
                  <p style={{ color: colors.textMuted, margin: '0 0 0.25rem 0', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={12} /> Estimated end: {formatDate(status.current.scheduled_end)}
                  </p>
                )}
                <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.8rem' }}>Started: {formatDate(status.current?.activated_at || status.current?.created_at)}</p>
              </div>
            </div>
            <div style={{ marginTop: '0.85rem' }}>
              <button onClick={stopMaintenance} disabled={actionLoading} style={{ ...btnGreen, opacity: actionLoading ? 0.5 : 1 }}>
                <Square size={13} />{actionLoading ? 'Stopping...' : 'End Maintenance Mode'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', borderLeft: `4px solid ${colors.odds}`, borderRadius: 6, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem' }}>
              <div style={{ width: 10, height: 10, background: colors.odds, borderRadius: '50%' }} />
              <span style={{ color: colors.odds, fontWeight: 700, fontSize: '0.9rem' }}>System is Online</span>
            </div>
            <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.83rem' }}>All users can log in and access the system normally.</p>
          </div>
        )}

        {/* Scheduled upcoming */}
        {status?.scheduled && !status?.is_active && (
          <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.3)', borderLeft: `4px solid ${colors.pending}`, borderRadius: 6, padding: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <Clock size={18} style={{ color: colors.pending, flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.pending, fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.3rem' }}>Scheduled Maintenance</div>
                <p style={{ color: colors.textMuted, margin: '0 0 0.35rem 0', fontSize: '0.85rem' }}>{status.scheduled.message}</p>
                <p style={{ color: colors.textMuted, margin: '0 0 0.15rem 0', fontSize: '0.8rem' }}>Starts: {formatDate(status.scheduled.scheduled_start)}</p>
                {status.scheduled.scheduled_end && (
                  <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.8rem' }}>Estimated end: {formatDate(status.scheduled.scheduled_end)}</p>
                )}
              </div>
            </div>
            <div style={{ marginTop: '0.85rem' }}>
              <button onClick={() => cancelScheduled(status.scheduled.id)} disabled={actionLoading} style={{ padding: '0.45rem 1rem', background: 'rgba(249,115,22,0.15)', border: `1px solid ${colors.pending}`, color: colors.pending, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem', opacity: actionLoading ? 0.5 : 1 }}>
                Cancel Scheduled Maintenance
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Start Maintenance */}
      {!status?.is_active && (
        <div style={card}>
          {!showStartForm ? (
            <button onClick={() => setShowStartForm(true)} style={btnDanger}><Play size={13} /> Start Maintenance Mode</button>
          ) : (
            <div>
              <h3 style={{ color: colors.textPrimary, margin: '0 0 1rem 0', fontSize: '0.95rem' }}>Start Maintenance Mode</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>

                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.5rem' }}>Timing</label>
                  <div style={{ display: 'flex', gap: '1.5rem' }}>
                    {[['immediate', 'Start Immediately'], ['scheduled', 'Schedule for Later']].map(([val, label]) => (
                      <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          value={val}
                          checked={scheduleType === val}
                          onChange={e => setScheduleType(e.target.value)}
                          style={{ accentColor: colors.gold }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {scheduleType === 'scheduled' && (
                  <div>
                    <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Start Time</label>
                    <input type="datetime-local" value={scheduledStart} onChange={e => setScheduledStart(e.target.value)} style={{ ...inputStyle, maxWidth: 300 }} />
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Estimated End Time (optional)</label>
                  <input type="datetime-local" value={scheduledEnd} onChange={e => setScheduledEnd(e.target.value)} style={{ ...inputStyle, maxWidth: 300 }} />
                </div>

                <div>
                  <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Message to Display</label>
                  <textarea
                    value={maintenanceMessage}
                    onChange={e => setMaintenanceMessage(e.target.value)}
                    rows={3}
                    placeholder="Enter message to show users..."
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={startMaintenance} disabled={actionLoading} style={{ ...btnDanger, opacity: actionLoading ? 0.5 : 1 }}>
                    {actionLoading ? 'Starting...' : scheduleType === 'immediate' ? 'Start Now' : 'Schedule'}
                  </button>
                  <button onClick={() => setShowStartForm(false)} style={btnCancel}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Site Warning */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: colors.textPrimary, margin: 0, fontSize: '0.95rem' }}>Site Warning</h3>
          <button onClick={loadWarning} style={btnPrimary}><RefreshCw size={14} /> Refresh</button>
        </div>

        {warningLoading ? (
          <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Loading...</p>
        ) : warningData?.is_active ? (
          <div style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.35)', borderLeft: `4px solid ${colors.pending}`, borderRadius: 6, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
              <AlertTriangle size={18} style={{ color: colors.pending, flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: colors.pending, fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.35rem' }}>Warning Active</div>
                <p style={{ color: colors.textMuted, margin: '0 0 0.4rem 0', fontSize: '0.85rem' }}>{warningData.warning.message}</p>
                <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.78rem' }}>Set: {formatDate(warningData.warning.created_at)}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.85rem' }}>
              <button onClick={() => { setShowWarningForm(true); }} style={btnPrimary}>Edit</button>
              <button onClick={clearWarning} disabled={warningActionLoading} style={{ ...btnDanger, opacity: warningActionLoading ? 0.5 : 1 }}>
                <Square size={13} />{warningActionLoading ? 'Clearing...' : 'Clear Warning'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', borderLeft: `4px solid ${colors.odds}`, borderRadius: 6, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ width: 10, height: 10, background: colors.odds, borderRadius: '50%' }} />
              <span style={{ color: colors.odds, fontWeight: 700, fontSize: '0.9rem' }}>No Active Warning</span>
            </div>
            <p style={{ color: colors.textMuted, margin: '0.35rem 0 0 0', fontSize: '0.83rem' }}>The login page has no warning banner.</p>
          </div>
        )}

        {(!warningData?.is_active || showWarningForm) && (
          <div>
            {!showWarningForm ? (
              <button onClick={() => setShowWarningForm(true)} style={btnPrimary}><Play size={13} /> Post a Warning</button>
            ) : (
              <div>
                <label style={{ display: 'block', color: colors.textMuted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>
                  {warningData?.is_active ? 'Update warning message' : 'Warning message'}
                </label>
                <textarea
                  value={warningMessage}
                  onChange={e => setWarningMessage(e.target.value)}
                  rows={3}
                  placeholder="e.g. Scheduled maintenance tonight 11pm–1am. Bets placed during this window will still be settled."
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical', marginBottom: '0.75rem' }}
                />
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={setWarning} disabled={warningActionLoading} style={{ ...btnPrimary, opacity: warningActionLoading ? 0.5 : 1 }}>
                    {warningActionLoading ? 'Saving...' : warningData?.is_active ? 'Update Warning' : 'Post Warning'}
                  </button>
                  <button onClick={() => { setShowWarningForm(false); if (warningData?.warning?.message) setWarningMessage(warningData.warning.message); }} style={btnCancel}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div style={{ background: 'rgba(100,120,255,0.08)', border: '1px solid rgba(100,120,255,0.25)', borderRadius: 8, padding: '1rem' }}>
        <h4 style={{ color: '#818cf8', margin: '0 0 0.6rem 0', fontSize: '0.9rem' }}>About Maintenance Mode</h4>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: colors.textMuted, fontSize: '0.82rem', lineHeight: 1.8 }}>
          <li>When maintenance mode is active, only admin accounts can log in.</li>
          <li>Non-admin users will see a maintenance message on the login page.</li>
          <li>Scheduled maintenance will automatically activate at the start time.</li>
          <li>You can end maintenance mode at any time.</li>
          <li>Site warnings appear on the login page for everyone but do not block login.</li>
          <li>Use warnings for notices like scheduled downtime or known issues.</li>
        </ul>
      </div>
    </div>
  );
}
